import React, { useState } from 'react';
import Avatar from './Avatar';
import { initials, n1 } from '../stats';

const MUTED = '#8b978a';

const tabStyle = (active) => ({
  padding: '8px 16px',
  borderRadius: 10,
  fontSize: 14,
  cursor: 'pointer',
  background: active ? '#eef5e9' : 'transparent',
  color: active ? '#2f5c31' : '#7b8a78',
  fontWeight: active ? 600 : 400,
});

const headCell = (extra) => ({
  padding: '12px 0',
  cursor: 'pointer',
  userSelect: 'none',
  ...extra,
});

// Spalte -> Wert, nach dem sortiert wird. "name" wird als Text verglichen.
const SORTABLE = {
  name: null,
  dmg: (p) => p.dmg,
  k: (p) => p.k,
  d: (p) => p.d,
  kd: (p) => p.kd,
  win: (p) => p.win,
};

const DataTable = ({
  list = [],
  matches,
  matchDetails,
  selected,
  hovered,
  onSelect,
  onHover,
  onUnhover,
  meName,
}) => {
  const [tab, setTab] = useState('ranking');
  const [sort, setSort] = useState('dmg');
  const [dir, setDir] = useState(-1);
  const [expandedMatch, setExpandedMatch] = useState(null);

  const setSortKey = (key) => {
    if (sort === key) {
      setDir(-dir);
      return;
    }
    setSort(key);
    setDir(key === 'name' ? 1 : -1);
  };

  const caret = (key) => (sort === key ? (dir === 1 ? ' ▲' : ' ▼') : '');

  const maxDmg = Math.max(1, ...list.map((p) => p.dmg));
  const sorted = [...list].sort((a, b) =>
    sort === 'name' ? dir * a.name.localeCompare(b.name) : dir * (SORTABLE[sort](a) - SORTABLE[sort](b))
  );

  const toggleMatch = (matchid) => setExpandedMatch((prev) => (prev === matchid ? null : matchid));

  const matchPlayers = (rows) =>
    (rows || [])
      .slice()
      .sort((a, b) => b.damage - a.damage)
      .map((player, index) => (
        <tr key={index} style={{ borderTop: '1px solid #f0f4ee' }}>
          <td style={{ padding: '7px 10px' }}>{player.name}</td>
          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{player.damage}</td>
          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{player.kills}</td>
          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{player.assists}</td>
          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{player.deaths}</td>
          <td style={{ padding: '7px 10px', textAlign: 'right' }}>{player.utility_damage}</td>
        </tr>
      ));

  const matchHead = (
    <thead>
      <tr style={{ fontSize: 11, letterSpacing: '.07em', textTransform: 'uppercase', color: MUTED }}>
        <th style={{ padding: '7px 10px', textAlign: 'left' }}>Name</th>
        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Damage</th>
        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Kills</th>
        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Assists</th>
        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Deaths</th>
        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Utility</th>
      </tr>
    </thead>
  );

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 2,
        background: '#fff',
        border: '1px solid #e4ece0',
        borderRadius: 18,
        boxShadow: '0 12px 34px -22px rgba(31,64,32,.5)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '18px 24px 14px',
          borderBottom: '1px solid #eef3ea',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={tabStyle(tab === 'ranking')} onClick={() => setTab('ranking')}>
            Player Ranking
          </div>
          <div style={tabStyle(tab === 'matches')} onClick={() => setTab('matches')}>
            Matches
          </div>
        </div>
        {tab === 'ranking' && (
          <div style={{ fontSize: 12, color: MUTED }}>Click a row for private stats</div>
        )}
      </div>

      {tab === 'ranking' && (
        <div className="otter-scroll">
          <div>
            <div
              className="otter-grid"
              style={{
                padding: '0 24px',
                fontSize: 11,
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                color: MUTED,
                borderBottom: '1px solid #f0f4ee',
              }}
            >
              <div style={{ padding: '12px 0' }}>#</div>
              <div style={headCell()} onClick={() => setSortKey('name')}>
                Player{caret('name')}
              </div>
              <div style={headCell()} onClick={() => setSortKey('dmg')}>
                Total damage{caret('dmg')}
              </div>
              <div style={headCell({ textAlign: 'right' })} onClick={() => setSortKey('k')}>
                Kills{caret('k')}
              </div>
              <div style={headCell({ textAlign: 'right' })} onClick={() => setSortKey('d')}>
                Deaths{caret('d')}
              </div>
              <div style={headCell({ textAlign: 'right' })} onClick={() => setSortKey('kd')}>
                K/D{caret('kd')}
              </div>
              <div
                style={headCell({ padding: '12px 0 12px 22px' })}
                onClick={() => setSortKey('win')}
              >
                Win rate{caret('win')}
              </div>
            </div>

            {sorted.length > 0 ? (
              sorted.map((p, i) => {
                const active = selected === p.name || hovered === p.name;
                return (
                  <div
                    key={p.name}
                    className="otter-grid"
                    onClick={() => onSelect(p.name)}
                    onMouseEnter={(e) => onHover(p.name, e)}
                    onMouseLeave={() => onUnhover(p.name)}
                    style={{
                      padding: '13px 24px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f4f7f2',
                      transition: 'background .15s',
                      background: active ? '#f4f9f0' : '#fff',
                    }}
                  >
                    <div
                      style={{ fontSize: 13, color: '#a3ada0', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {i + 1}
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}
                    >
                      <Avatar src={p.imgUrl} initials={initials(p.name)} size={28} font={11} />
                      <span
                        style={{
                          fontSize: 14.5,
                          fontWeight: p.name === meName ? 700 : 550,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.name}
                      </span>
                    </div>
                    <div style={{ paddingRight: 26 }}>
                      <div
                        style={{
                          height: 8,
                          borderRadius: 99,
                          background: '#f0f4ec',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            borderRadius: 99,
                            width: (p.dmg / maxDmg) * 100 + '%',
                            background: 'linear-gradient(90deg,#a8cf8d,#4f9153)',
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#6f7d6c',
                          marginTop: 5,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {p.dmg.toLocaleString('de-DE')}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: 'right',
                        fontSize: 14,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.k}
                    </div>
                    <div
                      style={{
                        textAlign: 'right',
                        fontSize: 14,
                        color: '#7c8a79',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {p.d}
                    </div>
                    <div
                      style={{
                        textAlign: 'right',
                        fontSize: 14,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {n1(p.kd)}
                    </div>
                    <div
                      style={{
                        paddingLeft: 22,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 8,
                          borderRadius: 99,
                          background: '#f0f4ec',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            borderRadius: 99,
                            width: p.win + '%',
                            background: p.win >= 50 ? '#4f9153' : '#c9a06d',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          width: 44,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: '#4e5c4c',
                        }}
                      >
                        {n1(p.win)}%
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '28px 24px', textAlign: 'center', color: MUTED, fontSize: 14 }}>
                No player statistics available. Database connection required.
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'matches' && (
        <div className="otter-scroll">
          <div>
            {matches && matches.length > 0 ? (
              matches.map((match) => {
                const details = (matchDetails || {})[match.matchid];
                const team1Name = details?.team1?.[0]?.team_name || 'Team 1';
                const team2Name = details?.team2?.[0]?.team_name || 'Team 2';
                const isExpanded = expandedMatch === match.matchid;

                return (
                  <div key={match.matchid} style={{ borderBottom: '1px solid #f4f7f2' }}>
                    <div
                      onClick={() => toggleMatch(match.matchid)}
                      className="otter-hoverable"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '190px 150px 1fr 90px',
                        alignItems: 'center',
                        padding: '13px 24px',
                        cursor: 'pointer',
                        fontSize: 14,
                        background: isExpanded ? '#f4f9f0' : '#fff',
                      }}
                    >
                      <div style={{ color: '#6f7d6c' }}>
                        {new Date(match.start_time).toLocaleString()}
                      </div>
                      <div>{match.mapname}</div>
                      <div>
                        <span style={{ fontWeight: match.winner === team1Name ? 700 : 400 }}>
                          {team1Name}
                        </span>
                        <span style={{ color: MUTED }}> vs </span>
                        <span style={{ fontWeight: match.winner === team2Name ? 700 : 400 }}>
                          {team2Name}
                        </span>
                      </div>
                      <div
                        style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {match.team1_score}:{match.team2_score}
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ background: '#f6faf3', padding: '16px 24px 20px' }}>
                        {[
                          [team1Name, details?.team1],
                          [team2Name, details?.team2],
                        ].map(([teamName, rows]) => (
                          <div key={teamName} style={{ marginBottom: 14 }}>
                            <div
                              style={{
                                fontSize: 12,
                                letterSpacing: '.08em',
                                textTransform: 'uppercase',
                                color: '#6b7a68',
                                marginBottom: 6,
                              }}
                            >
                              {teamName}
                            </div>
                            <table style={{ width: '100%', fontSize: 13 }}>
                              {matchHead}
                              <tbody>{matchPlayers(rows)}</tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '28px 24px', textAlign: 'center', color: MUTED, fontSize: 14 }}>
                No match data available. Database connection required.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
